import { Seo } from "@/components/Seo";
import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight, ArrowLeft, Phone, CheckCircle2 } from "lucide-react";
import { COMPANY } from "@/lib/site";
import { SERVICES, getService } from "@/lib/services";

export default function UslugaDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const s = getService(slug);

  if (!s) return <Navigate to="/usluge" replace />;
  if (s.external) return <Navigate to={s.external} replace />;

  const Icon = s.icon;
  const others = SERVICES.filter((x) => x.slug !== s.slug).slice(0, 3);

  const title = t(`services.items.${s.slug}.title`);
  const tagline = t(`services.items.${s.slug}.tagline`);
  const intro = t(`services.items.${s.slug}.intro`);
  const blocks = t(`services.items.${s.slug}.blocks`, { returnObjects: true });
  const includes = t(`services.items.${s.slug}.includes`, { returnObjects: true });
  const refs = t(`services.items.${s.slug}.refs`, { returnObjects: true });
  const captions = t(`services.items.${s.slug}.gallery`, { returnObjects: true });
  const galleryTitle = t(`services.items.${s.slug}.galleryTitle`, { defaultValue: t("serviceDetail.galleryDefault") });

  const blocksArr = Array.isArray(blocks) ? blocks : [];
  const includesArr = Array.isArray(includes) ? includes : [];
  const refsArr = Array.isArray(refs) ? refs : [];
  const captionsArr = Array.isArray(captions) ? captions : [];

  return (
    <div data-testid="usluga-detail-page">
      <Seo title={title} description={intro} path={`/usluge/${s.slug}`} />
      {/* HERO */}
      <section className="border-b border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12 pb-16 lg:pt-16 lg:pb-20">
          <Link
            to="/usluge"
            data-testid="back-to-usluge"
            className="inline-flex items-center gap-2 text-ink-soft hover:text-ink text-sm font-medium transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            {t("serviceDetail.back")}
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14 items-center mt-8">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-ink text-white flex items-center justify-center">
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div className="overline text-[#D92D20]">{tagline}</div>
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.98] font-bold text-ink mt-6">
                {title}
              </h1>
              <p className="text-ink-soft text-lg leading-relaxed mt-6 max-w-2xl">{intro}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link
                  to={`/prijava?usluga=${s.slug}`}
                  data-testid="usluga-detail-cta"
                  className="inline-flex items-center gap-2 bg-[#D92D20] text-white px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-[#B91C1C] transition-colors"
                >
                  {t("serviceDetail.requestQuote")}
                  <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                </Link>
                <a
                  href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 border border-ink/30 text-ink px-6 py-3.5 text-sm font-medium rounded-sm hover:bg-ink hover:text-white transition-colors"
                >
                  <Phone className="w-4 h-4" strokeWidth={1.75} />
                  {COMPANY.phone}
                </a>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="relative aspect-[4/3] overflow-hidden border border-hair shadow-xl">
                <img src={s.image} alt={title} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Description */}
          <div className="md:col-span-7">
            <div className="overline text-ink-soft">{t("serviceDetail.aboutService")}</div>
            <div className="text-ink-soft leading-relaxed mt-5 space-y-4 text-[17px] max-w-2xl">
              {blocksArr.map((p, k) => <p key={k}>{p}</p>)}
            </div>

            {includesArr.length > 0 && (
              <div className="mt-12">
                <div className="overline text-ink-soft">{t("serviceDetail.includesTitle")}</div>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {includesArr.map((it) => (
                    <li key={it} className="flex items-start gap-3 border border-hair bg-white p-4">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-[#D92D20] shrink-0" strokeWidth={2} />
                      <span className="text-ink text-[15px] leading-snug">{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* References */}
          <div className="md:col-span-5">
            <div className="border border-hair bg-white p-7 sticky top-24">
              <div className="overline text-ink-soft">{t("serviceDetail.refsTitle")}</div>
              <ul className="mt-5 space-y-4">
                {refsArr.map((r) => (
                  <li key={r} className="flex items-start gap-3 pb-4 border-b border-hair last:border-0 last:pb-0">
                    <span className="font-display text-[#D92D20] text-lg leading-none mt-0.5">◆</span>
                    <span className="text-ink text-[15px] leading-snug">{r}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/prijava?usluga=${s.slug}`}
                className="inline-flex items-center gap-2 mt-7 bg-ink text-white px-5 py-3 text-sm font-medium rounded-sm hover:bg-[#112240] transition-colors w-full justify-center"
              >
                {t("serviceDetail.startProject")}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {s.gallery?.length > 0 && (
        <section className="border-t border-hair">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
            <div className="overline text-ink-soft">{t("serviceDetail.galleryKicker")}</div>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink tracking-tight mt-3 leading-tight">
              {galleryTitle}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {s.gallery.map((src, k) => {
                const caption = captionsArr[k] || "";
                return (
                  <a
                    key={k}
                    href={src}
                    target="_blank"
                    rel="noreferrer"
                    className="group border border-hair bg-white hover-lift block overflow-hidden"
                    data-testid={`gallery-item-${k}`}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-secondary/40">
                      <img
                        src={src}
                        alt={caption}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-105"
                      />
                    </div>
                    <div className="px-4 py-3 flex items-center justify-between gap-3">
                      <span className="text-ink-soft text-sm leading-snug">{caption}</span>
                      <ArrowUpRight className="w-4 h-4 text-ink-soft shrink-0 group-hover:text-ink transition-colors" strokeWidth={1.75} />
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* OTHER SERVICES */}
      <section className="border-t border-hair" style={{ backgroundColor: "#FBF7F2" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <div className="overline text-ink-soft">{t("serviceDetail.otherServices")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
            {others.map((o) => {
              const OIcon = o.icon;
              const to = o.external || `/usluge/${o.slug}`;
              return (
                <Link
                  key={o.slug}
                  to={to}
                  className="group border border-hair bg-white p-6 hover-lift flex items-start gap-4"
                  data-testid={`other-service-${o.slug}`}
                >
                  <div className={`w-11 h-11 ${o.external ? "bg-[#D92D20]" : "bg-ink"} text-white flex items-center justify-center shrink-0`}>
                    <OIcon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-display font-semibold text-ink leading-snug">{t(`services.items.${o.slug}.title`)}</div>
                    <div className="text-ink-soft text-sm mt-1">{t(`services.items.${o.slug}.tagline`)}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
